import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Text, TextArea, View } from "@adobe/react-spectrum";
import { useTranslation } from "react-i18next";
import { ChatDots, DashLg, SendFill, XLg } from "react-bootstrap-icons";
import styles from "./comment_chat_window.module.css";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { ListMessageTypeRequest } from "../../../../../services/soap/message/requests/ListMessageTypeRequest";
import { MESSAGE_TYPES } from "../../../../../hooks/message";
import { ToastQueue } from '@adobe/react-spectrum';
import { usePreloadAssets } from "../../../../../hooks/UsePreloadAssets";
import { ContextSelection } from "../../../../../components/protected/Search/ContextSelection";
import type { ContextObject } from "../../../../../components/protected/Search/ContextSelection/types";
import { ListMessageRequest } from "../../../../../services/soap/message/requests/ListMessageRequest";
import { MoveMessageRequest } from "../../../../../services/soap/message/requests/MoveMessageRequest";
import { NewMessageRequest } from "../../../../../services/soap/message/requests/NewMessageRequest";
import { GetMessageRequest } from "../../../../../services/soap/message/requests/GetMessageRequest";
import ContactsFilterMultipleTag from "../../../../../components/protected/layout-components/FilterComponent/ContactsFilterMultipleTagComponent/ContactsFilterMultipleTag";
import { GetContactListRequest } from "../../../../../services/soap/main/requests/GetContactListRequest";
import { invokeNewMessageCreated } from "../../../../../utils/NavigationUtils";

export interface CommentChatWindowProps {
  isOpen: boolean;
  onClose: () => void;
  /** Target message object (should include id, treeHandle, messageGroup, and MESSAGE context fields when possible). */
  targetMessage: any;
  /** Currently open context id when the chat is launched from a context detail view. */
  currentContextId?: string | number;
}

type ChatItem = {
  id: string;
  role: "me" | "other" | "system";
  text: string;
  createdAt: number;
};

type NotifyUser = {
  id: string;
  value: string;
  contactName: string;
  label: string;
  /** System user id (CONTACT.sysUser) when available. */
  sysUserId?: string;
  /** System user login (CONTACT.sysUserName) when available. */
  userName?: string;
};

type StoredNotifyThreadRoot = {
  version: 1;
  users: Array<Pick<NotifyUser, "id" | "contactName" | "label">>;
};

const decodeHtmlEntities = (s: string): string =>
  s
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");

const stripHtml = (input: unknown): string => {
  // Preview-only sanitization for chat bubbles (not for rendering rich text).
  // Handles both raw HTML and HTML that is entity-encoded.
  const raw = String(input ?? "");
  const s = decodeHtmlEntities(raw);

  const withoutTags = s
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<head[\s\S]*?<\/head>/gi, "")
    .replace(/<[^>]+>/g, " ");

  // Some messages carry CSS as plain text (e.g. when HTML is encoded/flattened).
  const withoutCssRules = withoutTags
    .replace(/[a-z0-9\s,.:#_-]+\{[^}]*\}/gi, " ")
    .replace(/@media[\s\S]*?\}/gi, " ");

  return withoutCssRules.replace(/\s+/g, " ").trim();
};

/**
 * ListMessage rows may expose `date` / `created` as ISO strings, Date instances, or unix seconds/ms.
 * `Date.parse` alone mis-handles numeric epochs and some SOAP shapes, which breaks sort order (newest can jump to top).
 */
const parseListMessageCreatedMs = (raw: unknown): number => {
  if (raw == null || raw === "") {
    return NaN;
  }
  if (raw instanceof Date) {
    const t = raw.getTime();
    return Number.isFinite(t) ? t : NaN;
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw < 1e12 ? Math.round(raw * 1000) : Math.round(raw);
  }
  const s = String(raw).trim();
  if (!s) {
    return NaN;
  }
  const parsed = Date.parse(s);
  if (Number.isFinite(parsed)) {
    return parsed;
  }
  const n = Number(s);
  if (Number.isFinite(n)) {
    return n < 1e12 ? Math.round(n * 1000) : Math.round(n);
  }
  return NaN;
};

const compareChatItemsChronological = (a: ChatItem, b: ChatItem): number => {
  const t = a.createdAt - b.createdAt;
  if (t !== 0) {
    return t;
  }
  const na = Number(a.id);
  const nb = Number(b.id);
  if (Number.isFinite(na) && Number.isFinite(nb)) {
    return na - nb;
  }
  return a.id.localeCompare(b.id);
};

const hasExistingContext = (msg: any, currentContextId?: string | number): boolean =>
  Boolean(
    msg?.folderId ||
      msg?.folderRefId ||
      msg?.projectId ||
      msg?.activityRefId ||
      msg?.projectRefId ||
      msg?.activityId ||
      msg?.MESSAGE?.folderId ||
      msg?.MESSAGE?.folderRefId ||
      msg?.MESSAGE?.projectId ||
      msg?.MESSAGE?.activityRefId ||
      msg?.MESSAGE?.projectRefId ||
      msg?.MESSAGE?.activityId ||
      currentContextId,
  );

const normalizeNotifyUser = (entry: any): NotifyUser | null => {
  const id = String(entry?.id ?? entry?.value ?? entry?.contact ?? entry?.contactId ?? "").trim();
  const sysUserId = String(entry?.sysUserId ?? entry?.sysUser ?? "").trim() || undefined;
  const userName = String(entry?.userName ?? entry?.sysUserName ?? "").trim() || undefined;
  const label = String(
    entry?.contactName ??
      entry?.contact ??
      entry?.userName ??
      entry?.label ??
      entry?.name ??
      [entry?.name3, entry?.name1].filter(Boolean).join(" ") ??
      "",
  ).trim();

  if (!id) {
    return null;
  }

  return {
    id,
    value: id,
    contactName: label || id,
    label: label || id,
    ...(sysUserId ? { sysUserId } : {}),
    ...(userName ? { userName } : {}),
  };
};

const extractNotifyUsers = (msg: any): NotifyUser[] => {
  const raw =
    msg?.RECIPIENT?.USER ??
    msg?.MESSAGE?.RECIPIENT?.USER ??
    msg?.RECIPIENT?.NOTIFY ??
    msg?.MESSAGE?.RECIPIENT?.NOTIFY ??
    [];

  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return list.map(normalizeNotifyUser).filter(Boolean) as NotifyUser[];
};

export function CommentChatWindow({
  isOpen,
  onClose,
  targetMessage,
  currentContextId,
}: Readonly<CommentChatWindowProps>) {
  const { t } = useTranslation();
  const { store, messageService, mainService } = useDependency();
  const { folderStatusMapping, projectStatusMapping, activityStatusMapping } =
    usePreloadAssets();
  const [messageTypes, setMessageTypes] = useState<Array<{ id: string; type: string }>>(
    [],
  );
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [draft, setDraft] = useState("");
  const [items, setItems] = useState<ChatItem[]>([]);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [contextSelection, setContextSelection] = useState<ContextObject | null>(
    null,
  );
  const [isEditingContext, setIsEditingContext] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const [notifyUsers, setNotifyUsers] = useState<NotifyUser[]>([]);
  const shouldAutoScrollRef = useRef(true);

  const notifyPickerKey = useMemo(() => {
    const targetId = String(
      targetMessage?.id ?? targetMessage?.MESSAGE?.id ?? "comment-chat",
    );
    const notifyKey = notifyUsers.map((user) => user.id).join(",");
    return `${targetId}:${notifyKey}`;
  }, [notifyUsers, targetMessage]);

  const requiresContextSelection = useMemo(
    () => !hasExistingContext(targetMessage, currentContextId),
    [currentContextId, targetMessage],
  );

  const contextRequiredForSend = useMemo(
    () => requiresContextSelection || isEditingContext,
    [isEditingContext, requiresContextSelection],
  );

  /** When context is required, send is allowed once any node is chosen (folder, project, or activity). */
  const contextSelectionIncomplete = useMemo(() => {
    if (!contextRequiredForSend) return false;
    if (contextSelection === null) return true;
    const id = Number(contextSelection.contextId);
    return !Number.isFinite(id) || id <= 0;
  }, [contextRequiredForSend, contextSelection]);

  const resolvedContextId = useMemo(() => {
    if (!contextRequiredForSend) return "";
    return contextSelection?.contextId ?? "";
  }, [contextRequiredForSend, contextSelection]);

  const explicitContextId = useMemo(() => {
    const raw = currentContextId ?? "";
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [currentContextId]);

  const existingContextId = useMemo(() => {
    const msg = targetMessage?.MESSAGE ?? targetMessage ?? {};
    const raw =
      msg?.activityId ??
      msg?.projectId ??
      msg?.folderId;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? String(n) : "";
  }, [targetMessage]);

  const existingContextIdNumber = useMemo(() => {
    const n = Number(existingContextId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [existingContextId]);

  const effectiveExplicitContextId = useMemo(
    () => explicitContextId ?? existingContextIdNumber,
    [existingContextIdNumber, explicitContextId],
  );

  const fallbackGroupId = useMemo(() => {
    const raw = targetMessage?.messageGroup ?? targetMessage?.MESSAGE?.messageGroup;
    const value = String(raw ?? "").trim();
    return value || "";
  }, [targetMessage]);

  const fallbackTreeHandle = useMemo(() => {
    const raw = targetMessage?.treeHandle ?? targetMessage?.MESSAGE?.treeHandle;
    const value = String(raw ?? "").trim();
    return value || "";
  }, [targetMessage]);

  const threadQuery = useMemo(() => {
    const rawId = targetMessage?.id != null ? String(targetMessage.id).trim() : "";
    const messageId =
      targetMessage?.MESSAGE?.id != null ? String(targetMessage.MESSAGE.id).trim() : "";
    // Link/list rows can carry `id: "1"` while the authoritative id is in MESSAGE.id.
    const parent = messageId && (!rawId || rawId === "0" || rawId === "1") ? messageId : rawId || messageId || "";
    // Client: any conversation should be listed by threadId.
    // Do NOT treat messageGroup as threadId (they are different concepts).
    const threadId = targetMessage?.threadId ?? targetMessage?.MESSAGE?.threadId;

    return {
      parent,
      threadId: threadId ? String(threadId) : undefined,
    };
  }, [
    targetMessage?.id,
    targetMessage?.MESSAGE?.id,
    targetMessage?.threadId,
    targetMessage?.MESSAGE?.threadId,
  ]);

  const resolvedOriginalMessageId = useMemo(() => {
    const rawId = targetMessage?.id != null ? String(targetMessage.id).trim() : "";
    const messageId =
      targetMessage?.MESSAGE?.id != null ? String(targetMessage.MESSAGE.id).trim() : "";
    const chosen = messageId && (!rawId || rawId === "0" || rawId === "1") ? messageId : rawId;
    return chosen || threadQuery.parent || messageId || rawId || "";
  }, [targetMessage, threadQuery.parent]);

  const effectiveThreadId = useMemo(() => {
    // Client: threadId should be sufficient; fallback should be parent.
    const raw = targetMessage?.threadId ?? targetMessage?.MESSAGE?.threadId;
    const s = raw != null ? String(raw) : "";
    return s.trim() ? s : undefined;
  }, [targetMessage?.threadId, targetMessage?.MESSAGE?.threadId]);

  const notifyThreadRootStorageKey = useMemo(() => {
    const threadKey = String(
      effectiveThreadId ??
        threadQuery.threadId ??
        targetMessage?.threadId ??
        targetMessage?.MESSAGE?.threadId ??
        targetMessage?.id ??
        targetMessage?.MESSAGE?.id ??
        "",
    ).trim();
    return `atollon:commentChat:notifyThreadRoot:v1:${threadKey || "unknown"}`;
  }, [effectiveThreadId, threadQuery.threadId, targetMessage]);

  /**
   * Identity of “which message we’re commenting on”. Parent list refreshes often replace
   * `targetMessage` with a new object reference; this key must stay stable so the chat
   * does not wipe draft/history on every send.
   */
  const commentChatSessionKey = useMemo(() => {
    const parent =
      targetMessage?.id != null && String(targetMessage.id).trim() !== ""
        ? String(targetMessage.id)
        : targetMessage?.MESSAGE?.id != null && String(targetMessage.MESSAGE.id).trim() !== ""
          ? String(targetMessage.MESSAGE.id)
          : "";
    const th = String(
      targetMessage?.treeHandle ?? targetMessage?.MESSAGE?.treeHandle ?? "",
    ).trim();
    return `${parent}\u001f${th}`;
  }, [
    targetMessage?.id,
    targetMessage?.MESSAGE?.id,
    targetMessage?.treeHandle,
    targetMessage?.MESSAGE?.treeHandle,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    (async () => {
      try {
        setIsLoadingTypes(true);
        const res = await messageService.listMessageType(
          new ListMessageTypeRequest(store.Server, store.SessionId),
        );
        if (cancelled) return;
        if (res?.result === "OK" && Array.isArray(res.ITEM)) {
          const types = res.ITEM.filter((x: any) =>
            ["1", "6", "7", "12"].includes(String(x.type)),
          ).map((x: any) => ({ id: String(x.id), type: String(x.type) }));
          setMessageTypes(types);
        }
      } catch (e) {
        // Non-fatal — sending will fail with a clearer toast later if types are missing.
        console.error("Failed to load message types for comment chat", e);
      } finally {
        if (!cancelled) setIsLoadingTypes(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, messageService, store.Server, store.SessionId]);

  const loadThread = useCallback(async (options?: { silent?: boolean }) => {
    if (!isOpen) return;
    const silent = options?.silent === true;
    const treeHandle =
      targetMessage?.treeHandle ?? targetMessage?.MESSAGE?.treeHandle ?? "";

    // If we don't have a threadId (preferred) nor a parent fallback, we can't load anything.
    if (!effectiveThreadId && !threadQuery.parent) {
      setItems([]);
      return;
    }

    if (!silent) {
      setIsLoadingThread(true);
    }
    try {
      // If the selected message doesn't carry threadId (common in Communication list rows),
      // fetch message detail to resolve threadId first.
      let resolvedThreadId = effectiveThreadId;
      if (!resolvedThreadId && threadQuery.parent) {
        try {
          const detail = await messageService.getMessage(
            new GetMessageRequest(store.Server, store.SessionId, threadQuery.parent, undefined, "0"),
          );
          const tid = String((detail as any)?.MESSAGE?.threadId ?? "").trim();
          if (tid) {
            resolvedThreadId = tid;
          }
        } catch {
          // non-fatal; will fall back to parent query
        }
      }

      const shouldFilterByNode = !resolvedThreadId && Boolean(treeHandle);

      const res = await messageService.listMessage(
        new ListMessageRequest(store.Server, store.SessionId, {
          // Client: list conversations by threadId.
          // Only fall back to parent when threadId is missing (older/edge messages).
          parent: resolvedThreadId ? undefined : threadQuery.parent || undefined,
          threadId: resolvedThreadId,
          offset: "0",
          limit: "30",
          order: "down",
          orderBy: "created",
          // When threadId is available it should define the conversation scope by itself.
          // Adding node-filtering on top can hide valid thread rows that live on a different node.
          fromNode: shouldFilterByNode ? "true" : "false",
          treeHandle: shouldFilterByNode ? String(treeHandle) : undefined,
          tree: 0,
          showDeleted: false,
          showSent: false,
          showDrafts: false,
          showInbox: false,
          // Match legacy: showAll/showAllMy false.
          showAll: "false",
          showAllMy: "false",
          // Legacy request sets these explicitly as well.
          newOnly: "false",
          starred: "false",
        }),
      );
      if (
        typeof window !== "undefined" &&
        (window as any).__DEBUG_COMMENT_CHAT__ === true
      ) {
        // eslint-disable-next-line no-console
        console.log("[CommentChatWindow] listMessage response", res);
      }

      if (res?.result !== "OK" || !res?.MESSAGE) {
        if (!silent) {
          setItems([]);
        }
        return;
      }

      const raw = Array.isArray(res.MESSAGE) ? res.MESSAGE : [res.MESSAGE];
      const mapped: ChatItem[] = raw
        .map((m: any) => {
          const id = String(m?.id ?? "");
          const createdAtRaw = m?.date ?? m?.created ?? m?.CREATED;
          let createdAt = parseListMessageCreatedMs(createdAtRaw);
          if (!Number.isFinite(createdAt)) {
            createdAt = Date.now();
          }
          const text =
            // Prefer server-provided teaser/preview — these are meant for UI lists.
            stripHtml(m?.teaser) ||
            stripHtml(m?.PREVIEW?.content) ||
            stripHtml(m?.PLAIN_PREVIEW?.content) ||
            // Fallback to comment/html fields.
            stripHtml(m?.comment) ||
            stripHtml(m?.COMMENT?.string) ||
            stripHtml(m?.html) ||
            stripHtml(m?.HTML?.string) ||
            stripHtml(m?.SUBJECT?.string) ||
            stripHtml(m?.subject) ||
            "";

          if (!id || !text) return null;

          // Detect whether this message was authored by the current user.
          // ListMessage returns fields like `createdBy` (user id) and `userName`.
          const sender = String(m?.sender ?? m?.mailFrom ?? "").trim();
          const authorUserName = String(m?.userName ?? "").trim();
          const authorUserId = String(m?.createdBy ?? "").trim();

          const myUserName = String(store?.Username ?? "").trim();
          const myUserId = String((store as any)?.UserId ?? "").trim();

          const isMe =
            (myUserId && authorUserId && authorUserId === myUserId) ||
            (myUserName &&
              authorUserName &&
              authorUserName.toLowerCase() === myUserName.toLowerCase()) ||
            (myUserName &&
              sender &&
              sender.toLowerCase().includes(myUserName.toLowerCase()));

          const role: ChatItem["role"] = isMe ? "me" : "other";

          return {
            id,
            role,
            text,
            createdAt,
          };
        })
        .filter(Boolean) as ChatItem[];

      // Oldest at top; tie-break on id so order stays stable when timestamps match.
      mapped.sort(compareChatItemsChronological);
      setItems(mapped);
    } catch (e) {
      console.error("Failed to load comment thread", e);
      if (!silent) {
        setItems((prev) => [
          ...prev,
          {
            id: `sys-${Date.now()}`,
            role: "system",
            text: t("error_loading_data", {
              ns: "layout_components",
              defaultValue: "Error loading data",
            }),
            createdAt: Date.now(),
          },
        ]);
      }
    } finally {
      if (!silent) {
        setIsLoadingThread(false);
      }
    }
  }, [
    isOpen,
    messageService,
    store.Server,
    store.SessionId,
    (store as any)?.session?.userName,
    store?.Username,
    t,
    targetMessage?.treeHandle,
    targetMessage?.MESSAGE?.treeHandle,
    threadQuery.parent,
    threadQuery.threadId,
    effectiveThreadId,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    setDraft("");
    setItems([]);
    setContextSelection(null);
    setIsEditingContext(false);
    // Notify is treated as a thread-root selection (sticky for this chat thread),
    // not derived from the target message payload (which often doesn't include it).
    try {
      const raw = sessionStorage.getItem(notifyThreadRootStorageKey);
      if (raw) {
        const parsed: StoredNotifyThreadRoot = JSON.parse(raw);
        if (parsed?.version === 1 && Array.isArray(parsed.users)) {
          const restored = parsed.users
            .map((u) => normalizeNotifyUser(u))
            .filter(Boolean) as NotifyUser[];
          if (restored.length > 0) {
            setNotifyUsers(restored);
          } else {
            setNotifyUsers(extractNotifyUsers(targetMessage));
          }
        } else {
          setNotifyUsers(extractNotifyUsers(targetMessage));
        }
      } else {
        setNotifyUsers(extractNotifyUsers(targetMessage));
      }
    } catch {
      setNotifyUsers(extractNotifyUsers(targetMessage));
    }
    setIsMinimized(false);
    if (
      typeof window !== "undefined" &&
      (window as any).__DEBUG_COMMENT_CHAT__ === true
    ) {
      // eslint-disable-next-line no-console
      console.log("[CommentChatWindow] opened", {
        targetMessageId: targetMessage?.id ?? targetMessage?.MESSAGE?.id ?? null,
        threadId: threadQuery.threadId ?? null,
        parent: threadQuery.parent ?? null,
      });
    }
    // Load existing thread messages on open.
    void loadThread();
  }, [isOpen, commentChatSessionKey, loadThread, notifyThreadRootStorageKey, targetMessage]);

  useEffect(() => {
    if (!isOpen) return;
    try {
      const payload: StoredNotifyThreadRoot = {
        version: 1,
        users: notifyUsers.map((u) => ({
          id: String(u.id ?? "").trim(),
          contactName: String(u.contactName ?? "").trim(),
          label: String(u.label ?? u.contactName ?? u.id ?? "").trim(),
        })),
      };
      sessionStorage.setItem(notifyThreadRootStorageKey, JSON.stringify(payload));
    } catch {
      // ignore storage failures
    }
  }, [isOpen, notifyThreadRootStorageKey, notifyUsers]);

  useEffect(() => {
    if (!isOpen || notifyUsers.length === 0) {
      return;
    }

    const missingNameIds = notifyUsers
      .filter((user) => !user.contactName || user.contactName === user.id)
      .map((user) => user.id)
      .filter(Boolean);

    if (missingNameIds.length === 0) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const response = await mainService.getContactList(
          new GetContactListRequest(store.Server, store.SessionId, {
            offset: 0,
            limit: missingNameIds.length,
            onlyIds: false,
            showOnlyUc: false,
            onlyUser: true,
            conjuctionDistrGroups: 0,
            USERS: {
              ID: missingNameIds,
            },
          }),
        );

        if (cancelled || response?.result !== "OK" || !Array.isArray(response?.CONTACT)) {
          return;
        }

        const nameMap = new Map<string, string>(
          response.CONTACT.map((row: any) => [
            String(row.id),
            `${row?.name3 !== undefined ? row?.name3 : ""} ${row?.name1 ?? ""}`.trim(),
          ]),
        );

        setNotifyUsers((prev) =>
          prev.map((user) => {
            const resolvedName = nameMap.get(String(user.id));
            if (!resolvedName) {
              return user;
            }

            return {
              ...user,
              contactName: resolvedName,
              label: resolvedName,
            };
          }),
        );
      } catch (error) {
        console.error("Failed to resolve notify user names for comment chat", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, mainService, notifyUsers, store.Server, store.SessionId]);

  useEffect(() => {
    if (!isOpen || notifyUsers.length === 0) {
      return;
    }

    const missingSys = notifyUsers
      .filter((u) => !u.sysUserId || !u.userName)
      .map((u) => u.id)
      .filter(Boolean);

    if (missingSys.length === 0) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const response = await mainService.getContactList(
          new GetContactListRequest(store.Server, store.SessionId, {
            offset: 0,
            limit: missingSys.length,
            onlyIds: false,
            showOnlyUc: false,
            onlyUser: true,
            conjuctionDistrGroups: 0,
            USERS: {
              ID: missingSys,
            },
          }),
        );

        if (cancelled || response?.result !== "OK" || !Array.isArray(response?.CONTACT)) {
          return;
        }

        const byContactId = new Map<string, { sysUserId?: string; userName?: string }>(
          response.CONTACT.map((row: any) => [
            String(row.id),
            {
              sysUserId: row?.sysUser != null ? String(row.sysUser).trim() : undefined,
              userName: row?.sysUserName != null ? String(row.sysUserName).trim() : undefined,
            },
          ]),
        );

        setNotifyUsers((prev) =>
          prev.map((u) => {
            const resolved = byContactId.get(String(u.id));
            if (!resolved) return u;
            return {
              ...u,
              sysUserId: u.sysUserId ?? resolved.sysUserId,
              userName: u.userName ?? resolved.userName,
            };
          }),
        );
      } catch (error) {
        console.error("Failed to resolve notify sys user for comment chat", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, mainService, notifyUsers, store.Server, store.SessionId]);

  const updateAutoScrollFlag = useCallback(() => {
    const el = threadRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    // Consider "near bottom" within 80px.
    shouldAutoScrollRef.current = distanceFromBottom < 80;
  }, []);

  useEffect(() => {
    // Auto-scroll only when the user is already near bottom.
    if (!shouldAutoScrollRef.current) return;
    threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [items, isLoadingThread]);

  const canSend = useMemo(() => {
    if (!draft.trim()) return false;
    if (isSending) return false;
    if (contextRequiredForSend && contextSelectionIncomplete) return false;
    return true;
  }, [draft, isSending, contextRequiredForSend, contextSelectionIncomplete]);

  const createParams = useCallback((fieldName: string, value: any) => {
    // Keep API compatible with ContactsFilterMultipleTag.
    if (fieldName !== "notify") return;
    if (!value) {
      setNotifyUsers([]);
      return;
    }
    if (value instanceof Set) {
      setNotifyUsers(Array.from(value).map(normalizeNotifyUser).filter(Boolean) as NotifyUser[]);
      return;
    }
    if (Array.isArray(value)) {
      setNotifyUsers(value.map(normalizeNotifyUser).filter(Boolean) as NotifyUser[]);
      return;
    }
    setNotifyUsers([]);
  }, []);

  const normalizedNotifyRecipients = useMemo(() => {
    // For MESSAGE.NewMessage recipient USER entries, backend typically expects SYS user ids.
    // Prefer sysUserId when available, fall back to id.
    return (notifyUsers ?? []).map((u) => ({
      id: String(u.sysUserId ?? u.id),
      contactName: u.userName ?? u.contactName,
      CONTENT: u.contactName,
    }));
  }, [notifyUsers]);

  const resolveNotifyRecipientsForSend = useCallback(
    async (): Promise<Array<{ id: string; contactName?: string; CONTENT?: string }>> => {
      const users = notifyUsers ?? [];
      if (users.length === 0) return [];

      const missing = users
        .filter((u) => !u.sysUserId || !u.userName)
        .map((u) => String(u.id ?? "").trim())
        .filter(Boolean);

      if (missing.length === 0) {
        return users.map((u) => ({
          id: String(u.sysUserId ?? u.id),
          contactName: u.userName ?? u.contactName,
          CONTENT: u.contactName,
        }));
      }

      // Ensure we don't send contact ids if sys user ids are required.
      const response = await mainService.getContactList(
        new GetContactListRequest(store.Server, store.SessionId, {
          offset: 0,
          limit: missing.length,
          onlyIds: false,
          showOnlyUc: false,
          onlyUser: true,
          conjuctionDistrGroups: 0,
          USERS: { ID: missing },
        }),
      );

      const byContactId = new Map<string, { sysUserId?: string; userName?: string }>();
      if (response?.result === "OK" && Array.isArray(response?.CONTACT)) {
        response.CONTACT.forEach((row: any) => {
          byContactId.set(String(row.id), {
            sysUserId: row?.sysUser != null ? String(row.sysUser).trim() : undefined,
            userName: row?.sysUserName != null ? String(row.sysUserName).trim() : undefined,
          });
        });
      }

      return users.map((u) => {
        const resolved = byContactId.get(String(u.id));
        const sysUserId = u.sysUserId ?? resolved?.sysUserId ?? u.id;
        const userName = u.userName ?? resolved?.userName ?? u.contactName;
        return {
          id: String(sysUserId),
          contactName: userName,
          CONTENT: u.contactName,
        };
      });
    },
    [mainService, notifyUsers, store.Server, store.SessionId],
  );

  const resolveNotifyCustomTypeId = useCallback((): number | undefined => {
    // Client: choose custom type that is "Notify" (base type is Notice).
    // We only have (id,type) in messageTypes; so we rely on server ordering OR presence
    // of a "notify" entry in the full listMessageType response if you extend later.
    // For now: pick the first custom type with base type NOTE ("1") if available.
    const t1 = messageTypes.find((x) => String(x.type) === MESSAGE_TYPES.NOTE);
    if (!t1) return undefined;
    const n = Number(t1.id);
    return Number.isFinite(n) ? n : undefined;
  }, [messageTypes]);

  const handleSend = useCallback(async () => {
    if (!draft.trim()) return;
    if (isSending) return;

    if (contextRequiredForSend && contextSelectionIncomplete) {
      ToastQueue.negative(
        t("please_choose_context", {
          ns: "layout_components",
          defaultValue: "Please choose a context before sending.",
        }),
        { timeout: 3000 },
      );
      return;
    }

    if (isLoadingTypes || messageTypes.length === 0) {
      ToastQueue.negative(
        t("loading", { ns: "layout_components", defaultValue: "Loading…" }),
        { timeout: 1500 },
      );
      return;
    }

    const optimisticId = `local-${Date.now()}`;
    const text = draft.trim();
    setItems((prev) => {
      const maxPrev = prev.reduce((acc, it) => Math.max(acc, it.createdAt), 0);
      // Force strictly after existing rows so sort / refresh never places the bubble above older messages.
      const createdAt = Math.max(Date.now(), maxPrev) + 1;
      const next = [
        ...prev,
        { id: optimisticId, role: "me" as const, text, createdAt },
      ];
      next.sort(compareChatItemsChronological);
      return next;
    });
    setDraft("");

    try {
      setIsSending(true);
      const originalId = resolvedOriginalMessageId;
      const threadId =
        String(
          targetMessage?.threadId ??
            targetMessage?.MESSAGE?.threadId ??
            "",
        ) || undefined;

      // If original is outside context: enforce context picker and move original first.
      if (requiresContextSelection) {
        const newNode = String(resolvedContextId || "");
        if (!newNode) {
          throw new Error(
            t("please_choose_context", {
              ns: "layout_components",
              defaultValue: "Please choose a context before sending.",
            }),
          );
        }

        // Move original message (and ideally the whole thread) to chosen context.
        await messageService.moveMessage(
          new MoveMessageRequest(store.Server, store.SessionId, {
            id: originalId,
            messageId: originalId,
            newNode,
            doNotMoveThread: "0",
          }),
        );
      }

      const customTypeId = resolveNotifyCustomTypeId();
      const recipients = await resolveNotifyRecipientsForSend();
      const sendBinding = contextRequiredForSend
        ? resolvedContextId
          ? { contextId: resolvedContextId }
          : {}
        : fallbackGroupId && fallbackTreeHandle
          ? {
              group: fallbackGroupId,
              treeHandle: fallbackTreeHandle,
            }
          : existingContextId
            ? { contextId: existingContextId }
            : {};

      const response: any = await messageService.newMessage(
        new NewMessageRequest(store.Server, store.SessionId, {
          numberOfAttachments: 0,
          priority: 2,
          customType: customTypeId,
          type: Number(MESSAGE_TYPES.NOTE),
          // Bind to the original message
          parent: originalId || undefined,
          threadId,
          SUBJECT: { string: text },
          COMMENT: { string: text },
          RECIPIENT: {
            USER: recipients,
          },
          CONTACT_RELATIONS: {},
          ...sendBinding,
        }),
      );

      if (response?.result !== "OK") {
        throw new Error(response?.EXCEPTION?.message || "Failed to send message");
      }

      // Refresh the surrounding message list (without requiring manual refresh).
      // Mirrors global compose behavior: publish NEW_MESSAGE_CREATED which sets the sessionStorage flags.
      const reloadContext =
        requiresContextSelection
          ? String(resolvedContextId ?? "")
          : existingContextId
            ? String(existingContextId)
            : "";
      invokeNewMessageCreated(reloadContext, "message_created");
    } catch (e: any) {
      // Replace the optimistic bubble with a visible failure message.
      setItems((prev) =>
        prev.map((it) =>
          it.id === optimisticId
            ? {
                ...it,
                role: "system",
                text:
                  e?.message ||
                  t("error_sending_message", {
                    ns: "layout_components",
                    defaultValue: "Error sending message",
                  }),
              }
            : it,
        ),
      );
      return;
    } finally {
      setIsSending(false);
    }

    // Refresh thread without blanking the UI (no “Loading…” flash).
    void loadThread({ silent: true });
  }, [
    draft,
    isSending,
    isLoadingTypes,
    messageService,
    messageTypes,
    requiresContextSelection,
    resolvedContextId,
    contextSelectionIncomplete,
    existingContextId,
    fallbackGroupId,
    fallbackTreeHandle,
    notifyUsers,
    store.Server,
    store.SessionId,
    t,
    targetMessage,
    loadThread,
    resolveNotifyCustomTypeId,
  ]);

  const handleComposerKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== "Enter") {
        return;
      }

      if (!event.metaKey && !event.ctrlKey) {
        return;
      }

      event.preventDefault();
      if (canSend) {
        void handleSend();
      }
    },
    [canSend, handleSend],
  );

  if (!isOpen) return null;

  const titleText = t("comment", { ns: "layout_components", defaultValue: "Comment" });

  const panel = (
    <div className={styles.dock_root} role="dialog" aria-label={titleText} aria-modal="false">
      {isMinimized ? (
        <button
          type="button"
          className={styles.minimized_chip}
          onClick={() => setIsMinimized(false)}
          aria-expanded={false}
        >
          <ChatDots size={18} className={styles.minimized_icon} aria-hidden />
          <span className={styles.minimized_label}>{titleText}</span>
          {items.length > 0 && (
            <span className={styles.minimized_badge} aria-label={`${items.length} messages`}>
              {items.length > 99 ? "99+" : items.length}
            </span>
          )}
        </button>
      ) : (
        <div className={styles.panel}>
          <header className={styles.panel_header}>
            <div className={styles.header_titles}>
              <span className={styles.header_title}>{titleText}</span>
              <span className={styles.header_sub}>
                {t("comment_thread_hint", {
                  ns: "layout_components",
                  defaultValue: "Discussion on this message",
                })}
              </span>
            </div>
            <div className={styles.header_actions}>
              <button
                type="button"
                className={styles.icon_btn}
                onClick={() => setIsMinimized(true)}
                aria-label={t("minimize", { ns: "layout_components", defaultValue: "Minimize" })}
              >
                <DashLg size={18} />
              </button>
              <button
                type="button"
                className={styles.icon_btn}
                onClick={() => onClose()}
                aria-label={t("close", { ns: "layout_components", defaultValue: "Close" })}
              >
                <XLg size={16} />
              </button>
            </div>
          </header>

          <div className={styles.content}>
            {(requiresContextSelection ||
              existingContextId ||
              effectiveExplicitContextId != null ||
              isEditingContext) && (
              <View UNSAFE_className={styles.context_row}>
                <div className={styles.context_header}>
                  <Text UNSAFE_className={styles.context_label}>
                    {t("context", { ns: "layout_components", defaultValue: "Context" })}
                  </Text>
                  {!requiresContextSelection && !isEditingContext && (
                    <button
                      type="button"
                      className={styles.context_change_btn}
                      onClick={() => setIsEditingContext(true)}
                    >
                      {t("change", { ns: "layout_components", defaultValue: "Change" })}
                    </button>
                  )}
                </div>
                <div className={styles.context_picker}>
                  <ContextSelection
                    key={`comment-ctx-${String(effectiveExplicitContextId ?? "none")}`}
                    value={contextSelection}
                    onSelectionChange={(next) => {
                      setContextSelection(next);
                      if (!requiresContextSelection) {
                        setIsEditingContext(true);
                      }
                    }}
                    folderStatusMap={folderStatusMapping}
                    projectStatusMap={projectStatusMapping}
                    activityStatusMap={activityStatusMapping}
                    isReadOnly={!requiresContextSelection && !isEditingContext}
                    showBorders={false}
                    allowClearSelection={requiresContextSelection || isEditingContext}
                    allowAdd={false}
                    explicitContextId={effectiveExplicitContextId}
                    aria-label={t("context", { ns: "layout_components" })}
                  />
                </div>
                {contextSelectionIncomplete && (
                  <Text UNSAFE_className={styles.context_hint}>
                    {t("please_choose_context", {
                      ns: "layout_components",
                      defaultValue: "Choose a folder, project, or activity to store this comment.",
                    })}
                  </Text>
                )}
              </View>
            )}

            <div
              className={styles.thread}
              ref={threadRef}
              onScroll={updateAutoScrollFlag}
            >
              {isLoadingThread ? (
                <Text UNSAFE_className={styles.empty}>
                  {t("loading", { ns: "layout_components", defaultValue: "Loading…" })}
                </Text>
              ) : items.length === 0 ? (
                <Text UNSAFE_className={styles.empty}>
                  {t("no_comments_yet", {
                    ns: "layout_components",
                    defaultValue: "No comments yet.",
                  })}
                </Text>
              ) : (
                items.map((it) => (
                  <div
                    key={it.id}
                    className={
                      it.role === "me"
                        ? styles.bubble_me
                        : it.role === "other"
                          ? styles.bubble_other
                          : styles.bubble_system
                    }
                  >
                    {it.text}
                  </div>
                ))
              )}
              <div ref={threadEndRef} aria-hidden />
            </div>

            <div className={styles.composer}>
              <div className={styles.notify_row}>
                <div className={styles.notify_label}>
                  {t("notify", { ns: "layout_components", defaultValue: "Notify" })}
                </div>
                <div className={styles.notify_picker}>
                  <ContactsFilterMultipleTag
                    key={notifyPickerKey}
                    createParams={createParams}
                    setContactsSelected={notifyUsers}
                    fieldName={"notify"}
                    params={{
                      onlyIds: false,
                      showOnlyUc: false,
                      onlyUser: 1,
                      conjuctionDistrGroups: 0,
                      ORDER: "",
                    }}
                  />
                </div>
              </div>
              <div className={styles.composer_inner}>
                <TextArea
                  aria-label={t("comment", { ns: "layout_components", defaultValue: "Comment" })}
                  value={draft}
                  onChange={setDraft}
                  onKeyDown={handleComposerKeyDown}
                  placeholder={t("write_a_comment", {
                    ns: "layout_components",
                    defaultValue: "Write a comment…",
                  })}
                  UNSAFE_className={styles.textarea}
                />
                <button
                  type="button"
                  className={styles.send_btn}
                  onClick={handleSend}
                  disabled={!canSend}
                  aria-label={t("send", { ns: "layout_components", defaultValue: "Send" })}
                  title={t("send", { ns: "layout_components", defaultValue: "Send" })}
                >
                  <SendFill size={16} aria-hidden />
                </button>
              </div>
              <div className={styles.composer_hint}>
                {t("send_hint", {
                  ns: "layout_components",
                  defaultValue: "Ctrl/Cmd+Enter to send",
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(panel, document.body)
    : null;
}

